import React, { Dispatch, SetStateAction, useEffect, useState } from "react"
import { DeliveryOrderLine, Order } from "lib/types/generated/graphql-types"
import {
  Amount,
  AmountInput,
  Compensated,
  Id,
  InputPercentage,
  Name,
  PercentageButton,
  PercentageButtonWrapper,
  PriceWrapper,
  ProductImage,
  RowContainer,
  RowWrapper
} from "../Shared.styled"
import Money from "../../../../Money"
import Input from "../../../../Ui/Form/Input"
import { InputOrderLineCompensationWithAmount } from "lib/types/order"
import PrimaryButton from "../../../../Ui/Buttons/PrimaryButton"
import { useMutation } from "@apollo/client"
import UPDATE_COMPENSATION from "../../../../../graphql/mutations/order/compensation/UpdateCompensation"
import {
  calculateTaxAmount,
  compensationOrderLinesOutputToInput,
  getCompensationInput,
  toCompensationOrderLineFromState
} from "../Helpers/helpers"
import DineroFactory from "dinero.js"
import Dinero from "dinero.js"
import { NoticeBanner } from "../../../../Ui/Messages/NoticeBanner"
import { handleErrorMessages } from "helpers/errors"
import { handleImageError } from "helpers/image"
import { Compensations } from "../../../../../classes/order/Compensations"
import { Refunds } from "../../../../../classes/order/Refunds"
import { Releases } from "../../../../../classes/order/Releases"

type Props = {
  orderLine: DeliveryOrderLine
  order: Order
  currencyCode: string
  refetch: () => void
  setInputOrderLines: Dispatch<SetStateAction<InputOrderLineCompensationWithAmount[]>>
  inputOrderLines: InputOrderLineCompensationWithAmount[]
  clearOrderPercentage: () => void
  orderPercentage: number | undefined
}

const percentageArray = [5, 10, 15, 20, 25]

export const VariantRow = ({
  orderLine,
  order,
  currencyCode,
  setInputOrderLines,
  inputOrderLines,
  refetch,
  clearOrderPercentage,
  orderPercentage
}: Props) => {
  const [percentage, setPercentage] = useState<number>()
  const [customPercentage, setCustomPercentage] = useState<string>()
  const [showInput, setShowInput] = useState(false)

  const compensations = new Compensations(order.Compensations)
  const refunds = new Refunds(order.Refunds)
  const releases = new Releases(order.Releases)

  const refundedQuantity = refunds.orderLineRefundedQuantity(orderLine.orderLineId) ?? 0
  const orderLineReleased = releases.orderLineReleased(orderLine.orderLineId)

  const [updateCompensation, { loading: updateLoading }] = useMutation(UPDATE_COMPENSATION, {
    onCompleted: () => {
      refetch()
    },
    onError: (error) => {
      handleErrorMessages(error)
    }
  })

  const getOrderLine = () => {
    return order?.orderLines?.find((ol) => ol.id === orderLine.orderLineId)
  }

  const getAmount = (handleTax: boolean) => {
    const line = inputOrderLines.find(
      (inputOrderLine) => inputOrderLine.orderLineId === orderLine.orderLineId
    )
    if (handleTax && !order.isTaxIncludedInPrice && line) {
      return (
        line?.compensationAmount +
        calculateTaxAmount(line?.compensationAmount, line.taxPercentage, line.taxPercentageDecimals)
      )
    }
    return line?.compensationAmount ?? 0
  }

  const orderLineCompensatedAmount = compensations.orderLineTotal(orderLine.orderLineId)

  const updateOrderCompensation = () => {
    updateCompensation({
      variables: {
        compensationId: compensations.getPendingCompensation()?.id,
        input: {
          ...getCompensationInput(compensations.getPendingCompensation()),
          payment: {
            amount:
              (compensations.getPendingCompensation()?.payment?.amount ?? 0) +
              (getAmount(true) ?? 0)
          },
          orderLines: [
            ...compensationOrderLinesOutputToInput(
              compensations.getPendingCompensation()?.orderLines ?? []
            ),
            toCompensationOrderLineFromState(
              inputOrderLines.find(
                (inputOrderLine) => inputOrderLine.orderLineId === orderLine.orderLineId
              ),
              order.currencyCode
            )
          ]
        }
      }
    })
    setInputOrderLines(
      inputOrderLines.map((orderLine) => ({ ...orderLine, compensationAmount: 0 }))
    )
  }

  const setAmount = (amount: number) => {
    setInputOrderLines(
      inputOrderLines.map((inputOrderLine) =>
        inputOrderLine.orderLineId === orderLine.orderLineId
          ? {
              ...inputOrderLine,
              compensationAmount: amount
            }
          : inputOrderLine
      )
    )
  }

  const handlePercentageClick = (percentageInput: number) => {
    if (percentageInput !== percentage) {
      setCustomPercentage("")
      setPercentage(percentageInput)
      setAmount(Math.floor(getTotalAmount() * (percentageInput / 100)))
      clearOrderPercentage()
    }
  }

  const updateAmount = (value: number) => {
    !isNaN(value)
      ? setCustomPercentage(Math.floor((value / getTotalAmount()) * 100).toString())
      : setCustomPercentage("")
    setPercentage(undefined)
    setAmount(value)
    clearOrderPercentage()
  }

  const updateCustomPercentage = (percentage: string) => {
    if (
      (parseInt(percentage) && parseInt(percentage) < 101 && parseInt(percentage) > 0) ||
      isNaN(parseInt(percentage))
    ) {
      const amount =
        parseInt(percentage) === 100
          ? getTotalAmount() - orderLineCompensatedAmount
          : Math.floor(getTotalAmount() * (parseInt(percentage) / 100))
      setPercentage(undefined)
      setCustomPercentage(percentage)
      setAmount(amount)
      clearOrderPercentage()
    }
  }

  useEffect(() => {
    if (orderPercentage) {
      if (percentageArray.includes(orderPercentage)) {
        setPercentage(orderPercentage)
        setCustomPercentage("")
        return
      }
      setPercentage(undefined)
      setCustomPercentage(orderPercentage.toString())
    }
  }, [orderPercentage])

  const getTotalAmount = () => {
    return Dinero({
      amount: orderLine.totalAmount,
      currency: currencyCode as DineroFactory.Currency
    })
      .divide(orderLine.quantity)
      .multiply(orderLine.quantity - refundedQuantity)
      .getAmount()
  }

  if (refundedQuantity === orderLine.quantity || orderLineReleased) return null

  return (
    <RowContainer>
      {orderLineCompensatedAmount > 0 && (
        <>
          <Compensated>
            Previously compensated amount:{" "}
            <Money
              amount={orderLineCompensatedAmount}
              currencyUnit={order.currencyCode as DineroFactory.Currency}
            />
            {!order.isTaxIncludedInPrice && " (Ex. VAT)"}
          </Compensated>
          <hr />
        </>
      )}

      <RowWrapper key={orderLine.orderLineId}>
        <div>
          <ProductImage src={getOrderLine()?.imageUrl as string} onError={handleImageError} />
          <Name>{getOrderLine()?.name}</Name>
          <PriceWrapper>
            {orderLine.totalDiscountAmount > 0 && (
              <span>
                <Money
                  amount={orderLine.totalAmount + orderLine.totalDiscountAmount}
                  currencyUnit={currencyCode as DineroFactory.Currency}
                />
              </span>
            )}
            <b>
              <Money
                amount={getTotalAmount()}
                currencyUnit={currencyCode as DineroFactory.Currency}
              />
              {!order.isTaxIncludedInPrice && " (ex. VAT)"}
            </b>
          </PriceWrapper>
          <div>Order quantity: {orderLine.quantity}</div>
          {refundedQuantity > 0 && <div>Refunded quantity: {refundedQuantity}</div>}
          <Id>
            <b>Variant ID:</b> <span>{getOrderLine()?.productVariantId}</span>
          </Id>
        </div>
        <span>
          <Amount>
            <b>Compensation</b>
            <PercentageButtonWrapper>
              {percentageArray.map((value) => (
                <PercentageButton
                  key={value}
                  $selected={value === percentage}
                  $orderPercentage={!!orderPercentage}
                  onClick={() => handlePercentageClick(value)}
                >
                  {value}%
                </PercentageButton>
              ))}
              {(showInput || !customPercentage) && (
                <InputPercentage
                  name={"percentage"}
                  onBlur={() => setShowInput(false)}
                  onWheelCapture={(e) => e.currentTarget.blur()}
                  onFocus={() => setShowInput(true)}
                  type={"number"}
                  value={customPercentage}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    updateCustomPercentage(e.target.value)
                  }
                />
              )}
              {!showInput && customPercentage && (
                <PercentageButton
                  $orderPercentage={!!orderPercentage}
                  $selected={true}
                  onClick={() => setShowInput(true)}
                >
                  {customPercentage}%
                </PercentageButton>
              )}
            </PercentageButtonWrapper>
            <AmountInput>
              <Input
                name={"amount"}
                value={getAmount(false)}
                type="number"
                placeholder={"Amount"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  updateAmount(parseInt(e.target.value))
                }
              />
              <div>
                <b>Formatted:</b>{" "}
                {getAmount(false) ? (
                  <Money
                    amount={getAmount(false) ?? 0}
                    currencyUnit={currencyCode as DineroFactory.Currency}
                  />
                ) : (
                  0
                )}
              </div>
              {!order.isTaxIncludedInPrice && <b>ex. VAT</b>}
            </AmountInput>
          </Amount>
        </span>
      </RowWrapper>

      {getAmount(false) > getTotalAmount() && (
        <NoticeBanner type={"important"}>Amount to high</NoticeBanner>
      )}
      {compensations.pending() && (
        <PrimaryButton
          handleClick={updateOrderCompensation}
          loading={updateLoading}
          disabled={!getAmount(false) || getAmount(false) === 0}
        >
          Add order line
        </PrimaryButton>
      )}
    </RowContainer>
  )
}
